import { cancel, select, takeEvery } from 'redux-saga/effects';
import {
  DruckRequestPayload,
  getGeschaeftsart,
  GlobalAction,
  GlobalActions,
  GlobalPut,
} from '@ps20-frontend/shop-integration-redux';
import {
  App,
  mapLeistungen,
  mapToBffVorvertragsdatenDruck,
  mapZusatzoptionenDruck,
  numberToGermanNumberFormattedString,
} from '@ps20-frontend/frontend-models';
import {
  Feature,
  SpartenType,
  VorschaedenDruck,
  VwgDruckdaten,
  ZusatzoptionenDruck,
} from '@ps20/bff-models';
import { PS20Logger } from '@ps20-frontend/logging';
import { getProduktlinienDruck, mapGefahren, mapLinienpreis } from '@ps20-frontend/druckdaten-lib';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { mapGesamtbeitragIfJaehrlichAndBeitragIsWrong } from '@ps20-frontend/preis-lib';
import { selectFeatureToggle } from '@ps20-frontend/selector';
import {
  selectSelectedProduktlinie,
  selectZusatzoptionItems,
} from '../../app/shared/selectors/produkt.selector';
import { selectVorschaeden } from '../../app/shared/selectors/vorschaeden.selector';
import { Vorschaeden } from '../reducers/vorschaeden.reducer';
import { IAppState } from '../appState';
import { isVorschaedenVisibleByValue } from '../../app/shared/selectors/gebaeude.selector';

export function* watchDruckdatenChanged() {
  yield takeEvery(GlobalActions.DRUCKDATEN_REQUESTED, safeSaga(onDruckdatenRequested, App.VWG));
}

export function* onDruckdatenRequested(action: GlobalAction<DruckRequestPayload>) {
  try {
    const state: IAppState = yield select();
    const appIds: string[] = action.payload.appIds;

    if (!appIds.includes(App.VWG)) {
      yield cancel();
      return;
    }

    const vwgDruckdaten: VwgDruckdaten = mapDruckdaten(state);
    GlobalPut.getInstance().put(new GlobalActions().druckdatenGenerated(vwgDruckdaten));
  } catch (e) {
    PS20Logger.error(App.VWG, e as Error, 'Druckdatensaga.onDruckdatenRequested()');
    yield cancel();
  }
}

function mapDruckdaten(state: IAppState): VwgDruckdaten {
  const vorschaeden: Vorschaeden = selectVorschaeden(state);
  const zusatzoptionenDruck: Array<ZusatzoptionenDruck> = mapZusatzoptionenDruck(
    selectZusatzoptionItems(state),
  );
  const ausgewaehlteProduktlinie = selectSelectedProduktlinie(state);
  const vorteilsRabattActivated = selectFeatureToggle(state, Feature.VORTEILSRABATT);
  return {
    angebotsId: state.angebotsId,
    antragsnummer: state.antragsnummer,
    longText: ausgewaehlteProduktlinie.langtext,
    shortText: ausgewaehlteProduktlinie.kurztext,
    rbdAktuell: state.taasInfo.rbdNachlass,
    treuebonus: state.taasInfo.tbNachlass,
    vorteilsRabatt: vorteilsRabattActivated ? state.taasInfo.vorteilsrabatt || 0 : undefined,
    angebotsdatum: vorteilsRabattActivated ? state.vertragsoptionen.angebotsdatum : undefined,
    zaehlstuecke: state.rabattstufe,
    spartenType: SpartenType.WOHNGEBAEUDE,
    beginn: null, // kommt vom Checkout
    ablauf: null, // kommt vom Checkout
    gesamtbeitrag: numberToGermanNumberFormattedString(
      mapGesamtbeitragIfJaehrlichAndBeitragIsWrong(
        state.vertragsoptionen.zahlungsweise,
        state.gesamtbeitragMitRabatt,
        state.jahresbeitrag,
        App.VWG,
      ),
    ),
    selbstbeteiligung: state.vertragsoptionen.selbstbeteiligung,
    vertragsdauer: null,
    linienpreis: mapLinienpreis(state.produkt),
    leistungen: mapLeistungen(ausgewaehlteProduktlinie, SpartenType.WOHNGEBAEUDE),
    zusatzoptionenDruck,
    vorschaedenDruck: mapVorschaeden(vorschaeden, state.gebaeude.baujahr),
    vorversicherungenDruck: null,
    gefahrenDruck: mapGefahren(state.produkt),
    produktlinien: getProduktlinienDruck(state.produkt),
    zuersGefaehrdungsklasse:
      state.gisDaten.zuersGefaehrdungsklasse != null
        ? state.gisDaten.zuersGefaehrdungsklasse.toString()
        : '',
    vorvertragsnummern: mapToBffVorvertragsdatenDruck(state.vertragsoptionen.vorvertragsNummern),
    vorvertragGekuendigt: state.vertragsoptionen.vorvertragGekuendigt,
    abweichungVomZielmarkt: false,
    beibehalten: state.vertragsoptionen.beibehalten,
    gebaeudeDruck: {
      adresse: {
        plz: state.risikoort.plz,
        ort: state.risikoort.ort,
        strasse: state.risikoort.strasse,
        hausnummer: state.risikoort.hausnummer,
        longtitude: state.risikoort.longtitude,
        latitude: state.risikoort.latitude,
      },
      gebkategorie: state.merkmale.gebkategorie,
      baujahr: state.gebaeude.baujahr,
      wohnflaecheqm: state.gebaeude.wohnflaeche,
      kellerVorhanden: state.merkmale.keller,
      kellerMitWohnflaeche: mapKellerMitWohnflaeche(state),
      leerstehend: state.merkmale.nichtDauerhaftBewohnt,
      dachbelag: state.merkmale.dachbelag,
      holzhaus: state.merkmale.holzhaus,
      solarthermie: state.merkmale.solarthermie,
      photovoltaik: state.merkmale.photovoltaik,
      flachdach: state.merkmale.flachdach,
      geothermie: state.merkmale.geothermie,
      ferienhaus: state.merkmale.ferienhaus,
      schwimmbad: state.merkmale.schwimmbad,
      denkmalschutz: state.merkmale.denkmalschutz,
      nebengebaeude: state.gebaeude.nebengebaeude.vorhanden,
      nebengebaeudeqm: state.gebaeude.nebengebaeude.gesamtflaeche,
      feuerrohbau: state.merkmale.feuerrohbau,
      gleitenderNeuwert: state.gisDaten.gleitenderNeuwert,
    },
    sicherungsglaeubigerDruck: [], // kommt vom Checkout,
    extremwetterschutzSelbstbeteiligung: state.extremeWetterschutzSelbstbeteiligung,
    kooperation: state.vertragsoptionen.selectedKooperation,
    geschaeftsart: getGeschaeftsart(state.vertragsoptionen),
  };
}

function mapKellerMitWohnflaeche(state: IAppState): boolean {
  return state.merkmale.kellerMitWohnflaeche === null ? false : state.merkmale.kellerMitWohnflaeche;
}

function mapVorschaeden(vorschaden: Vorschaeden, baujahr: number): VorschaedenDruck {
  return {
    vorhanden: isVorschaedenVisibleByValue(baujahr),
    elementar: vorschaden.elementarVorschaden.includeInDruck
      ? vorschaden.elementarVorschaden.count
      : 0,
    feuer: vorschaden.feuerVorschaden.includeInDruck ? vorschaden.feuerVorschaden.count : 0,
    leitungswasser: vorschaden.leitungswasserVorschaden.includeInDruck
      ? vorschaden.leitungswasserVorschaden.count
      : 0,
    sturmhagel: vorschaden.sturmhagelVorschaden.includeInDruck
      ? vorschaden.sturmhagelVorschaden.count
      : 0,
  };
}
